import { useRef, useState, useEffect } from 'react';
import { Play, Pause, Volume2, AlertCircle } from 'lucide-react';
import { formatTime } from '@/lib/utils';
import { normalizeMediaSrc } from '@/lib/mediaUrl';

interface AudioPlayerProps {
  src: string;
  className?: string;
}

export function AudioPlayer({ src: rawSrc, className = '' }: AudioPlayerProps) {
  const src = normalizeMediaSrc(rawSrc);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMetadata = () => setDuration(audio.duration);
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onEnded = () => setPlaying(false);
    const onError = () => setError(true);

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      audio.play();
    }
    setPlaying(!playing);
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    audio.currentTime = ratio * duration;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (error) {
    return (
      <div className={`flex items-center gap-2 bg-surface rounded-xl border border-line p-3 ${className}`}>
        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
        <span className="text-xs text-fg-subtle">Audio non disponible</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 bg-surface rounded-xl border border-line p-3 sm:p-4 ${className}`}>
      <audio ref={audioRef} src={src} preload="metadata" />

      <button
        onClick={togglePlay}
        className="w-10 h-10 bg-green-500/15 rounded-full flex items-center justify-center flex-shrink-0 hover:bg-green-500/25 transition-colors"
      >
        {playing ? (
          <Pause className="w-4 h-4 text-green-400" />
        ) : (
          <Play className="w-4 h-4 text-green-400 ml-0.5" />
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div
          className="h-2 bg-overlay-2 rounded-full cursor-pointer overflow-hidden"
          onClick={handleSeek}
        >
          <div
            className="h-full bg-green-500 rounded-full transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between mt-1.5">
          <span className="text-[10px] text-fg-faint">{formatTime(Math.floor(currentTime))}</span>
          <span className="text-[10px] text-fg-faint">{formatTime(Math.floor(duration))}</span>
        </div>
      </div>

      <Volume2 className="w-4 h-4 text-fg-faint flex-shrink-0 hidden sm:block" />
    </div>
  );
}
